import navigateToCampaignVersion from 'common/src/modules/campaigns/utils/navigateToCampaignVersion';
import {CampaignStep} from 'common/src/modules/campaignWorkflow/campaignWorkflow.enums';
import {DTControlMode} from 'common/src/modules/decisionTree/treeTypes';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {CampaignsAgentsToolsNames} from '../copilotCampaigns.enums';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';

const allowedSteps = Object.values(CampaignStep).filter(
  (step) => step !== CampaignStep.TearsheetPreview,
);

const allowedModes = Object.values(DTControlMode).filter(
  (mode) => mode !== DTControlMode.TearSheets && mode !== DTControlMode.AdsGenerationHistory,
);

const inputSchema = z.object({
  campaignId: z.number().describe('The ID of the campaign to navigate to.'),
  versionNumber: z
    .number()
    .nullable()
    .optional()
    .nullable()
    .describe('The version of the campaign, or undefined for the latest version'),
  steps: z
    .array(z.enum(allowedSteps as [string, ...string[]]))
    .nullable()
    .optional()
    .nullable()
    .describe('The steps to navigate to, separated by commas'),
  mode: z
    .enum(allowedModes as [string, ...string[]])
    .nullable()
    .optional()
    .nullable()
    .describe(`The mode to navigate to`),
});

function navigateToCampaignVersionTool(store: CLStore) {
  async function execute({
    input: {campaignId, versionNumber, steps, mode},
    toolUtils: {logger},
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    const modeToTabForcing: Partial<Record<DTControlMode, string>> = {
      [DTControlMode.Comments]: CampaignStep.CampaignLogic,
      [DTControlMode.Issues]: CampaignStep.CampaignLogic,
      [DTControlMode.Preview]: CampaignStep.CampaignLogic,
      [DTControlMode.AnalyticsDashboard]: CampaignStep.Insights,
    };

    const forcedTab = modeToTabForcing[mode as DTControlMode];
    const finalSteps = forcedTab ? [forcedTab] : steps;

    await navigateToCampaignVersion({
      getState: store.getState,
      campaignId,
      versionNumber,
      steps: finalSteps?.join(','),
      mode: mode as DTControlMode,
      logger,
    });

    return toolResult(`Navigated to campaign${versionNumber ? ` version ${versionNumber}` : ''}`);
  }

  return tool({
    name: CampaignsAgentsToolsNames.NavigateToCampaignVersion,
    description: 'Navigate to a campaign and optional version',
    displayName: 'Navigating',
    execute,
    inputSchema,
  });
}

export default navigateToCampaignVersionTool;
